import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LEVELS, LEVELS_EN } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";

export default function Levels() {
  const { lang } = useLanguage();
  const t = STRINGS[lang].levelsSection;
  const levels = lang === "en" ? LEVELS_EN : LEVELS;

  return (
    <section id="niveis" className="border-y border-white/5 bg-[#070B14] py-28 lg:py-36" data-testid="levels-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-2xl"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
          <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            {t.title[0]}
            <em className="italic text-blue-400">{t.title[1]}</em>
          </h2>
        </motion.div>

        <Tabs defaultValue="iniciante" data-testid="levels-tabs">
          <TabsList className="mb-10 flex h-auto w-fit flex-wrap gap-1 rounded-full border border-white/10 bg-[#0B101C] p-1.5">
            {levels.map((level) => (
              <TabsTrigger
                key={level.id}
                value={level.id}
                data-testid={`level-tab-${level.id}`}
                className="rounded-full px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors duration-300 data-[state=active]:bg-blue-400 data-[state=active]:text-[#05070D]"
              >
                {level.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {levels.map((level) => (
            <TabsContent key={level.id} value={level.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="grid gap-12 lg:grid-cols-2"
                data-testid={`level-panel-${level.id}`}
              >
                <div>
                  <h3 className="font-serif text-2xl text-blue-50 sm:text-3xl">{level.headline}</h3>
                  <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-300/90">{level.desc}</p>
                  <div className="mt-8 inline-flex items-start gap-3 rounded-xl border border-blue-500/20 bg-blue-500/5 p-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400/80">{t.resultLabel}</span>
                    <p className="text-sm text-blue-100/90">{level.outcome}</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {level.milestones.map((milestone, i) => (
                    <li key={i} className="flex items-start gap-4 border-b border-white/5 pb-4">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-blue-500/30">
                        <Check className="h-3.5 w-3.5 text-blue-400" />
                      </span>
                      <span className="text-sm leading-relaxed text-slate-300">{milestone}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}
